import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { toast } from 'sonner'

import { env } from '@/env.config'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'

class ApiConnector {
  instance: AxiosInstance
  onMessage?: (message: string) => void = undefined
  onErrorMessage?: (message: string) => void = undefined

  constructor() {
    const instance = axios.create({
      baseURL: env.VITE_BACKEND_URL,
    })

    // Setup interceptors
    instance.interceptors.request.use(this.onRequest, this.onErrorResponse)
    instance.interceptors.response.use(this.onResponse, this.onErrorResponse)

    this.instance = instance

    this.onMessage = toast.success
    this.onErrorMessage = toast.error
  }

  /**
   * Request interceptor
   * - Loads JWT token and adds it to the request headers
   * - Adds timeout to get requests (15 seconds)
   */
  onRequest = async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const { method } = config

    // Add Authorization header
    const { token } = this.onLoadToken()
    if (token) config.headers.Authorization = `Bearer ${token}`

    // Add timeout to get requests
    if (method === 'get') config.timeout = 15000

    return config
  }

  /**
   * Response interceptor
   * - Shows toast message if response has a message
   */
  onResponse = (response: AxiosResponse): AxiosResponse => {
    // Show toast message
    const { data } = response
    if (data && data.message && this.onMessage) this.onMessage(data.message)

    return response
  }

  /**
   * Error response interceptor
   * - Refreshes token if it's expired and retries the request
   * - Shows toast message if response has a message
   * - Throws error if it's a network error
   */
  onErrorResponse = async (error: AxiosError | Error): Promise<AxiosError> => {
    if (axios.isAxiosError(error)) {
      const { config, response, code } = error

      // Check if token is expired
      if (response?.status === 401) {
        if (!config) throw error
        const { token } = await this.onRefreshToken()

        // Retry the original request with the new token
        config.headers.Authorization = `Bearer ${token}`
        return axios(config)
      }

      // Show toast message
      if (this.onErrorMessage)
        if (response && response.data) {
          this.onErrorMessage(response.data.errors.message ?? 'Erro de sistema')
        } else if (code === 'ERR_NETWORK') {
          this.onErrorMessage('Falha de conexão com servidor')
        }
    } else {
      console.error(error)
    }

    throw error
  }

  /**
   * Load token
   * - Loads the token from the store
   */
  onLoadToken = () => {
    const { token } = useAuthStore.getState().onGetTokens()
    return { token }
  }

  /**
   * Refresh token
   * - Refreshes the token using the refresh token saved in the store
   * - Updates the token in the store
   */
  onRefreshToken = async () => {
    const { token } = await useAuthStore.getState().onRefreshTokens()
    return { token }
  }
}

const apiConn = new ApiConnector().instance

export { apiConn }
